import { useEffect, useState } from 'react';
import Card from './../../common/Card';
import { useTranslation } from 'react-i18next';
import Choice from '../../common/Choice';
import Input from '../../common/Input';
import '../../../assets/css/pages/stores/upload-file.css';
import Button from '../../common/Button';
import Dropzone from '../../common/Dropzone';
import { TAX_INCLUDED_TYPE } from '../../../utils/constants/string';
import { useLocation, useNavigate } from 'react-router-dom';
import { Workbook } from 'exceljs';
import {
    fetchCompetitorChainsByStoreCode,
    fetchExpandSalesFloors,
    fetchItemCategories,
    fetchPackingCategories,
    fetchSaleTypes,
    fetchSources,
    setLoader,
} from '../../../redux/actions/api';
import { useDispatch, useSelector } from 'react-redux';
import { discountValidation, validateExcelInput } from '../../../utils/helpers/validation';
import {
    ChangeRequestControllerService,
    ErrorMessage,
    PriceSearchControllerService,
    PriceSearchRequest,
    VariantProductControllerService,
} from '../../../services';
import { setToastsError, setToastsSuccess } from '../../../redux/actions/common';
import {
    setExcelValidation,
    setIsContinue,
    setIsVariantProductsContinue,
    setShowLinkedItem,
    setVariantProducts,
} from '../../../redux/actions/modal';
import ViewTable from '../../tables/ViewTable';
import { getExcelUploadTable } from '../../tables/table';
import { getPercentagePrice, getSellingPrices, getTaxExcludedPrice } from '../../../utils/helpers/formula';
import apiErrorHandler, { apiValidationErrorHandler, getErrorItems } from '../../../api/apiErrorHandler';
import { clearErrors } from '../../../redux/actions/error';
import { setChangeRequestType } from '../../../redux/actions/storage';
import { RequiredBadge } from '../../common/RequiredBadge';
import { formatToYYYYMMDD } from '../../../utils/helpers/date';
import DuplicateModal from '../../modals/DuplicateModal';

const DEFAULT_STATE = {
    radioChecked: 'excluded',
    discountRate: 0,
    file: '',
    resultData: [],
    isShow: false,
    data: [],
    nbpbkbnItems: [],
    requestFormData: [],
    isShowFiftyDiscount: false,
    fiftyItems: [],
    // uploadExcelLimit: 200,

    // 重複確認モーダル
    duplicateModalIsShow: false,
    duplicateModalProps: null,
};

const UploadExcel = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useNavigate();
    const location = useLocation();

    const [state, setState] = useState(DEFAULT_STATE);
    const {
        radioChecked,
        discountRate,
        file,
        resultData,
        requestFormData,
        // uploadExcelLimit,
        duplicateModalIsShow,
        duplicateModalProps,
    } = state;

    const [formData, setFormData] = useState(null);

    const constants = useSelector((state) => state.constants);
    const { expandSalesFloors, packingCategories, saleTypes, sources, competitorChains } = constants;

    const storage = useSelector((state) => state.storage);
    const { changeRequestType, isCompetitor, isFreshness, isHead, isFlyer } = storage;

    const modal = useSelector((state) => state.modal);
    const { isContinue, isVariantProductsContinue } = modal;

    const table = getExcelUploadTable(expandSalesFloors, packingCategories, saleTypes);

    useEffect(() => {
        dispatch(clearErrors());
        dispatch(fetchItemCategories());
        dispatch(fetchExpandSalesFloors());
        dispatch(fetchPackingCategories());
        dispatch(fetchSaleTypes());
        dispatch(fetchSources());
        dispatch(fetchCompetitorChainsByStoreCode());
        if (isHead) {
            dispatch(setChangeRequestType(PriceSearchRequest.changeRequestType.FLYER));
        }
    }, [dispatch, location]);

    const handleChangeInput = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleClearForm = () => {
        setState(DEFAULT_STATE);
    };

    const handleComplete = () => {
        if (isHead) {
            history('/change-request/not_confirm');
            return;
        }
        history('/change-request/requesting');
    };

    useEffect(() => {
        if (file) {
            const readExcelFile = async () => {
                const workbook = new Workbook();
                const buffer = await file.arrayBuffer();
                await workbook.xlsx.load(buffer);
                const formData = [];

                workbook.eachSheet((sheet, id) => {
                    if (id === 1) {
                        sheet.eachRow((row, rowNumber) => {
                            if (rowNumber > 1) {
                                formData.push({
                                    id: rowNumber,
                                    jan: row.values[1],
                                    expandSalesFloor: expandSalesFloors.find((item) => item.label === row.values[6])?.value,
                                    discountedRequestSellingPrice: row.values[7],
                                    packingCategory: packingCategories.find((item) => item.label === row.values[8])?.value,
                                    saleType: saleTypes.find((item) => item.label === row.values[9])?.value,
                                    salesStartDate: formatToYYYYMMDD(row.values[10]),
                                    salesEndDate: formatToYYYYMMDD(row.values[11]),
                                    otherSpecialReports: row.values[12],
                                    competitorStoreCode: row.values[13],
                                    requesterName: row.values[14],
                                    source: sources.find((item) => item.label === row.values[15])?.value,
                                });
                            }
                        });
                    }
                });

                setFormData(formData);
            };

            readExcelFile();
        }
    }, [file]);

    const handleUpload = async () => {
        const rowCount = formData.length;
        // TODO: limitテスト
        // if (rowCount - 1 > uploadExcelLimit) {
        //     dispatch(clearErrors());
        //     dispatch(setToastsError(`一度に${uploadExcelLimit}件まで登録可能です。${uploadExcelLimit}件以内で登録してください。`));
        //     return;
        // }
        if (rowCount === 0) {
            dispatch(clearErrors());
            dispatch(setToastsError('Excelに商品を入力してください。'));
            return;
        }
        if (validateExcelInput(dispatch, formData)) {
            return;
        }

        dispatch(setLoader(true));
        dispatch(clearErrors());
        PriceSearchControllerService.search({
            jans: formData.map((item) => item.jan),
            changeRequestType: 'COMPETITOR',
            limit: rowCount,
        })
            .then(handleSubmit)
            .catch((error) => {
                apiErrorHandler(dispatch, error);
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const handleSubmit = async (apiResponse) => {
        const requestData = [];
        const errors = [];
        const data = apiResponse.data;
        dispatch(clearErrors());
        dispatch(setLoader(true));
        for (const row of formData) {
            const item = data?.find((item) => item.jan == row.jan);
            if (item) {
                // 商品情報をマージ
                const request = {
                    ...row,
                    ...item,
                };

                // 税込の場合、税抜価格を計算
                if (radioChecked === 'included') {
                    request.discountedRequestSellingPrice = getTaxExcludedPrice(request.discountedRequestSellingPrice, request.taxRate);
                }

                // 割引率が設定されている場合、割引価格を計算
                if (discountRate > 0) {
                    request.discountedRequestSellingPrice = getPercentagePrice(request.discountedRequestSellingPrice, discountRate);
                }

                // 割引率
                // ※ 割引率はフロント側でのみ使用されているため、
                // 通常であればAPIでサーバーに送る必要はないが、
                // 割引率がどれくらい利用されているかを調査したいとの要望により、
                // 割引率をAPIのリクエストに含め、APIのアクセスログで見れるようにする
                request.discountRate = discountRate;

                requestData.push(request);
            } else {
                errors.push({
                    rowNumber: row.id,
                    message: '存在しない商品です。',
                });
            }
        }

        const dataWithSellingPrice = await getSellingPrices(dispatch, requestData);
        if (!dataWithSellingPrice) {
            dispatch(setLoader(false));
            return;
        }

        if (errors.length > 0) {
            dispatch(
                setExcelValidation({
                    isShow: true,
                    items: errors,
                }),
            );
            dispatch(setLoader(false));
            return;
        }

        handleChangeInput('requestFormData', dataWithSellingPrice);

        validation(dataWithSellingPrice);
    };

    const validation = async (data, step = 0) => {
        dispatch(clearErrors());
        dispatch(setLoader(true));

        if (!isHead) {
            // 乖離チェック
            if (await discountValidation(dispatch, data, isFreshness)) {
                dispatch(setLoader(false));
                return;
            }
        }

        // 類似商品チェック
        if (await variantProdauctCheck(data)) {
            return;
        }

        // 重複チェック
        duplicateCheck(data);
    };

    useEffect(() => {
        dispatch(clearErrors());
        if (isContinue) {
            // 乖離モーダル表示後に処理を続行する場合
            dispatch(setIsContinue(false));

            (async () => {
                // 類似商品チェック
                if (await variantProdauctCheck(requestFormData)) {
                    return;
                }

                // 重複チェック
                duplicateCheck(requestFormData);
            })();
        }
        if (isVariantProductsContinue) {
            // 類似商品があっても登録処理を続行
            dispatch(setIsVariantProductsContinue(false));

            // 重複チェック
            duplicateCheck(requestFormData);
        }
    }, [dispatch, isContinue, isVariantProductsContinue, location]);

    const variantProdauctCheck = async (data) => {
        dispatch(setLoader(true));
        if (isCompetitor || isFlyer) {
            const jans = data.map((data) => data.jan);
            const competitorStoreCode = data.map((data) => data.competitorStoreCode);
            const competitorChainCodes =
                competitorChains
                    ?.filter((item) => item.competitorChainCode === competitorStoreCode[0].substring(0, 4))
                    .map((item) => item.competitorChainCode) ?? [];

            try {
                const variantProducts = await VariantProductControllerService.getVariantProducts({
                    jans: jans,
                    competitorChainCode: competitorChainCodes[0],
                });
                if (variantProducts.data.length > 0) {
                    dispatch(
                        setVariantProducts({
                            isShow: true,
                            items: variantProducts.data,
                        }),
                    );
                    dispatch(setLoader(false));
                    return true;
                }
            } catch (error) {
                dispatch(setLoader(false));
                apiErrorHandler(dispatch, error, t('message.error.registration'));
                return true;
            }
        }
        dispatch(setLoader(false));
        return false;
    };

    // 重複チェック
    const duplicateCheck = async (requestData) => {
        try {
            dispatch(setLoader(true));

            // 重複チェックAPIを呼ぶ
            const responseData = await ChangeRequestControllerService.checkDuplicateChangeRequest(
                changeRequestType,
                requestData.map((item) => ({
                    jan: item.jan,
                    competitorStoreCode: isCompetitor ? item.competitorStoreCode : null,
                    discountedRequestSellingPrice: item.discountedRequestSellingPrice,
                    salesStartDate: item.salesStartDate,
                    salesEndDate: item.salesEndDate,
                })),
            );

            dispatch(setLoader(false));

            // チェック結果を取得
            const duplicateItems = getErrorItems(responseData, requestData, ErrorMessage.errorType.DUPLICATE);

            // 重複が0件の場合
            if (!duplicateItems.length) {
                // 登録処理を行う
                handleStore(requestData);
            }
            // 重複が1件以上の場合
            else {
                const close = () => {
                    setState((prevState) => ({
                        ...prevState,

                        duplicateModalIsShow: false,
                        duplicateModalProps: null,
                    }));
                };

                // 重複確認モーダルを開く
                setState((prevState) => ({
                    ...prevState,

                    duplicateModalIsShow: true,
                    duplicateModalProps: {
                        dataTable: duplicateItems,
                        isFreshness: isFreshness,
                        isContinuation: false,

                        // 「OK」ボタンが押された
                        onOk: () => {
                            // 重複確認モーダルを閉じる
                            close();

                            // 登録処理を行う
                            handleStore(requestData);
                        },

                        // モーダルを閉じた or 「キャンセル」ボタンが押された
                        onClose: () => {
                            // 重複確認モーダルを閉じる
                            close();
                        },
                    },
                }));
            }
        } catch (error) {
            dispatch(setLoader(false));
            apiErrorHandler(dispatch, error);
        }
    };

    const handleStore = async (requestData) => {
        dispatch(setLoader(true));

        const request = isHead
            ? ChangeRequestControllerService.createChangeRequestFlyer('EXCEL', requestData)
            : ChangeRequestControllerService.createChangeRequestCompetitor('EXCEL', requestData);

        // 登録処理
        await request
            .then((response) => {
                // 連動商品が登録されていればモーダルを表示する
                if (!response) {
                    dispatch(setShowLinkedItem(true));
                }
                dispatch(setToastsSuccess('登録が完了しました。'));
                handleClearForm();
                setState({
                    ...DEFAULT_STATE,
                    resultData: requestData,
                });
            })
            .catch((error) => {
                apiValidationErrorHandler(dispatch, error, requestData, t('message.error.registration'));
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    return (
        <div className="upload-registration">
            <div className="okc-inner">
                <div className="okc-card fade-in">
                    <Card type="modal_card">
                        <div className="row okc-card__row mb-0">
                            <label className="col-lg-2 mt-lg-2 align-self-start">{t('upload_registration.select_price_type.title')}</label>
                            <div className="col-lg-10 competitive-store__radios">
                                <div className="row m-0 competitive-store__row">
                                    <Choice
                                        column="col-xl-3 col-lg-4 col-md-5"
                                        type="radio"
                                        data={TAX_INCLUDED_TYPE}
                                        onChange={(value) =>
                                            setState((prevState) => ({
                                                ...prevState,
                                                radioChecked: value[0],
                                            }))
                                        }
                                        checked={[radioChecked]}
                                        value={radioChecked}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row okc-card__row mb-0">
                            <label className="col-lg-2 mt-lg-2 align-self-start">{t('upload_registration.discount_rate.title')}</label>
                            <div className="col-lg-10">
                                <div className="m-0 discount-rate">
                                    <Input
                                        className="form-control form-control--input"
                                        value={discountRate}
                                        onChange={(value) => handleChangeInput('discountRate', value)}
                                        id="discountRate"
                                        type="text"
                                        maxLength="3"
                                        autoComplete="off"
                                    />
                                    <span>％</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
                <div className="okc-title c-slide-top">
                    {t('upload_registration.box.select_file.label')}
                    <span className="requird-badge-span">
                        <RequiredBadge />
                    </span>
                </div>
                <div className="okc-input-file c-slide-top">
                    <div className="okc-upload">
                        <Dropzone file={file} onChange={(value) => handleChangeInput('file', value)} />
                    </div>
                </div>
                <div className="d-flex justify-content-end c-slide-top mb-5">
                    <Button className="btn btn--primary upload-registration__submit" onClick={handleUpload} disabled={!file}>
                        <span className="btn-text">{t('button.upload')}</span>
                    </Button>
                    <Button className="btn btn--secondary ml-2" id="btn_clear" onClick={handleClearForm}>
                        <span className="btn-text">{t('button.clear')}</span>
                    </Button>
                    <Button
                        className={`btn btn--primary btn-block ml-2 ${!(resultData.length !== 0) ? 'btn--disabled' : ''}`}
                        onClick={handleComplete}
                        disabled={!(resultData.length !== 0)}
                    >
                        <span className="btn-text">{t('button.completion')}</span>
                    </Button>
                </div>
                {resultData.length !== 0 && (
                    <>
                        <div className="text-center okc-table-title">
                            <span>登録一覧</span>
                        </div>
                        <ViewTable heads={table.heads} body={table.body} dataTable={resultData} />
                    </>
                )}
            </div>

            {/* 重複確認モーダル */}
            {duplicateModalIsShow && <DuplicateModal {...duplicateModalProps} />}
        </div>
    );
};
export default UploadExcel;
